import { LogLevel } from '../diagnostics/log-level';
import { EnvironmentModule } from '../manifest/environment-modules';
import { ManifestLoader } from '../manifest/manifest-loader';
import { PerformanceTracker } from '../performance/performance-tracker';
import { AccessibilityManager } from './accessibility-manager';
import { AssetManager } from './asset-manager';
import { LocaleSet, LocalizationManager, LocalizationManagerOptions } from './localization-manager';
import { NativeQ } from './native-q';
import { PowerShell } from './powershell';

/**
 * Interface for manifest loading.
 */
export interface ManifestLoadingOptions {
    /**
     * Specify shell or module name.
     */
    name: string;

    /**
     * Specify the version of shell or module.
     */
    version: string;

    /**
     * Specify the name of PowerShell module.
     */
    powerShellModuleName?: string;

    /**
     * Specify the name of PowerShell prefix.
     */
    powerShellPrefix?: string;

    /**
     * Specify angular-cli production state used by the module loading only.
     */
    isProduction?: boolean;

    /**
     * Specify shell origin URL for sideloading debugging.
     */
    shellOrigin?: string;
}

/**
 * The enum switch to use predefined endpoints.
 */
export enum RuntimePowerShellEndpoint {
    /**
     * Not specify.
     */
    InlineScript = 0,

    /**
     * The default endpoint. Microsoft.PowerShell
     */
    Default = 1,

    /**
     * The SME endpoint. Microsoft.SME.PowerShell
     */
    Sme = 2
}

/**
 * Interface for runtime options.
 */
export interface RuntimeOptions {
    /**
     * Enable websocket if true.
     */
    websocket?: boolean;

    /**
     * Enable SSH websocket if true.
     */
    sshWebsocket?: boolean;

    /**
     * Force to use module functions instead of embedded script on PowerShell calls.
     */
    powerShellEndpoint?: RuntimePowerShellEndpoint | string;

    /**
     * Disable style injection if true.
     */
    disableStyleInjection?: boolean;

    /**
     * Disable accessibility if true.
     */
    disableAccessibility?: boolean;

    /**
     * Configure CSS version for supporting v2 that is 400% zoom
     */
    cssV2?: boolean;

    /**
     * Determines if it is react extension or not. If React extension, will not load any css from shell.
     */
    reactExtension?: boolean;
}

/**
 * Class to initialize and the SME environment
 *  (Localized string cannot be used in this class due to initialization phase when the strings are not ready yet.)
 * @dynamic
 */
export class CoreEnvironment {
    /**
     * The localization manager once the environment has been initialized
     */
    public static localizationManager: LocalizationManager<void>;
    public static accessibilityManager: AccessibilityManager;
    public static assetManager: AssetManager;

    /**
     * Initializes the environment with manifest loading mode.
     *
     * @param manifestOptions the name of manifest loading options.
     * @param localizationOptions the options to initialize the Localization Manager
     * @param runtimeOptions the optional runtime options.
     */
    public static initialize(
        manifestOptions: ManifestLoadingOptions,
        localizationOptions: LocalizationManagerOptions,
        runtimeOptions?: RuntimeOptions
    ): Promise<void> {
        if (!manifestOptions || !manifestOptions.name) {
            // no localization.
            throw new Error('CoreEnvironment.initialize() - Argument error: manifestOptions.');
        }
        PerformanceTracker.coreEnvironmentInitStarted();
        PerformanceTracker.initializeLighthouseMetricsTrackers();

        const InitOptions: MsftSme.MsftSmeInit = {
            mode: MsftSme.EnvironmentMode.LoadEmbedded,
            moduleName: manifestOptions.name,
            moduleVersion: manifestOptions.version,
            powerShellModuleName: manifestOptions.powerShellModuleName,
            powerShellPrefix: manifestOptions.powerShellPrefix,
            isProduction: manifestOptions.isProduction,
            sessionId: 'N/A',
            logLevel: MsftSme.consoleDebug() === null ? LogLevel.Warning : MsftSme.consoleDebug(),
            sessionExpiration: 0,
            performanceProfile: false,
            developerGuide: false,
            experiments: MsftSme.experiments(),
            connectivityLevel: '',
            isShell: false,
            shellVersion: null,
            gatewayApiVersion: null,
            gatewayPlatform: null
        };
        if (manifestOptions.name === EnvironmentModule.nameOfShell) {
            // shell manifest loading
            InitOptions.isShell = true;
            InitOptions.mode = MsftSme.EnvironmentMode.Load;
            InitOptions.sessionId = MsftSme.newGuid();
        } else if (manifestOptions.isProduction) {
            // module on the production using the same site origin from location information
            // if not specified by manifestOptions.shellOrigin.
            InitOptions.shellOrigin = manifestOptions && manifestOptions.shellOrigin ? manifestOptions.shellOrigin : window.location.origin;
        } else {
            // module side-loading manifest. non production environment accept any shell origin.
            InitOptions.shellOrigin = '*';
        }

        const self = MsftSme.self();
        self.Init = InitOptions;

        // enable websocket stream query only if requested.
        if (runtimeOptions && runtimeOptions.websocket) {
            self.Init.websocket = true;
        }

        // enable SSH websocket stream query only if requested.
        if (runtimeOptions && runtimeOptions.sshWebsocket) {
            self.Init.sshWebsocket = true;
        }

        // enable websocket stream query only if requested.
        if (runtimeOptions && runtimeOptions.powerShellEndpoint) {
            if (typeof runtimeOptions.powerShellEndpoint === 'string') {
                self.Init.powerShellEndpoint = <string>runtimeOptions.powerShellEndpoint;
            } else {
                const type = <RuntimePowerShellEndpoint>runtimeOptions.powerShellEndpoint;
                switch (type) {
                    case RuntimePowerShellEndpoint.Default:
                        self.Init.powerShellEndpoint = PowerShell.defaultPowerShellEndpoint;
                        break;
                    case RuntimePowerShellEndpoint.Sme:
                        self.Init.powerShellEndpoint = PowerShell.smePowerShellEndpoint;
                        break;
                }
            }
        }

        CoreEnvironment.localizationManager = new LocalizationManager<any>(localizationOptions);

        if (!runtimeOptions || !runtimeOptions.disableStyleInjection) {
            CoreEnvironment.assetManager = new AssetManager(CoreEnvironment.localizationManager, runtimeOptions?.cssV2, runtimeOptions?.reactExtension);
        }

        const deferred = NativeQ.defer<void>();
        PerformanceTracker.manifestLoadStarted();
        ManifestLoader.loadManifest()
            .then(() => {
                PerformanceTracker.localizationStarted();
                return CoreEnvironment.localization();
            })
            .then(() => {
                if (!runtimeOptions || !runtimeOptions.disableAccessibility) {
                    PerformanceTracker.accessibilityManagerStarted();
                    CoreEnvironment.accessibilityManager = new AccessibilityManager();
                }
                deferred.resolve();
            })
            .catch(deferred.reject)
            .finally(() => PerformanceTracker.coreEnvironmentInitCompleted());
        return deferred.promise;
    }

    /**
     * Validate and load localized strings if the localeId doesn't match with current locale Id.
     * @param localeSet the local information set.
     */
    public static moduleLoadLocale(localeSet: LocaleSet): Promise<void> {
        if (CoreEnvironment.localizationManager.localeId.id !== localeSet.id) {
            CoreEnvironment.localizationManager.saveLocale(localeSet);
            return CoreEnvironment.localization();
        }

        return NativeQ.resolved<void>();
    }

    /**
     * Initialize and load localization data by option settings.
     */
    private static localization(): Promise<void> {
        const self = MsftSme.self();
        CoreEnvironment.localizationManager.updateDocumentLanguage();
        return CoreEnvironment.localizationManager.fetchLocalizedStrings()
            .toPromise()
            .then(strings => {
                self.Resources.strings = strings;
            });
    }
}
